import { RefObject, useCallback } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    canNestGroupUnderParent,
    MAX_ACCOUNT_GROUP_DEPTH,
} from "@common/constants/domains/accounting/accountGroups";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ScopeInsertPosition } from "../draftOperations";
import { DraftGroup } from "../draftState";
import { useEditorCellFocus } from "../useEditorCellFocus";
import { NestedAddScope } from "./nestedTableMeta";
import { resolveAddFanOut } from "./nestedAddScope";
import { CoaEditorCommand } from "./coaEditorCommand";
import { NestedEditorHost } from "./nestedRowRefusal";
import { PendingAddEntry } from "./nestedPendingAdds";
import { scrollNestedRowIntoView } from "./scrollNestedRowIntoView";

export const NESTED_GROUP_DEPTH_LIMIT_MESSAGE = `Groups can be nested up to ${MAX_ACCOUNT_GROUP_DEPTH} levels deep.`;

interface UseNestedEditorAddActionsParams {
    tableRef: RefObject<ViewTableHandle | null>;
    clearFilters: () => void;
    // Needed to depth-gate inline creation consistently with picker and backend validation.
    groups: DraftGroup[];
    addAccountInScope: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        position?: ScopeInsertPosition;
        codeText: string | null;
    }) => string;
    suggestCreateCodeText: (params: {
        type: AccountType;
        targets: Array<{ entityId: number; parentGroupId: string | null }>;
    }) => string | null;
    addGroupInScope: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        position?: ScopeInsertPosition;
    }) => string;
    // Merge-view cells stamp the display-key row id, not the draft id.
    resolveFocusRowId?: (draftId: string) => string;
    host: NestedEditorHost;
    // Workspace only: the click mints draft rows and registers them; the write
    // waits for the name.
    onPendingAdd?: (entry: PendingAddEntry) => void;
    onOperation?: (command: CoaEditorCommand) => void;
}

export interface NestedEditorAddActions {
    handleAddAccount: (scope: NestedAddScope) => void;
    handleAddGroup: (scope: NestedAddScope) => void;
}

// Owns the nested editor's per-row add concerns: both "Add account" and
// "Add group" create a row directly in the host's scope (no modal), expand the
// host so the appended row is visible, then focus its inline name cell for
// immediate entry. Extracted from
// ChartOfAccountsTemplateNestedEditor to keep that component focused on wiring +
// rendering.
export const useNestedEditorAddActions = ({
    tableRef,
    clearFilters,
    groups,
    addAccountInScope,
    suggestCreateCodeText,
    addGroupInScope,
    resolveFocusRowId,
    host,
    onPendingAdd,
    onOperation,
}: UseNestedEditorAddActionsParams): NestedEditorAddActions => {
    const { requestFocus } = useEditorCellFocus(
        ({ rowId }) => scrollNestedRowIntoView(tableRef, rowId),
        resolveFocusRowId,
    );
    const { error: errorToast } = useToaster();

    const handleAddAccount = useCallback(
        (scope: NestedAddScope) => {
            // Clear any active search/column filters first: a freshly-added
            // account has a blank name and prefilled code that may not match the
            // active query, which would exclude it from the rendered tree and
            // leave it unreachable for focus (and silently blocking Save).
            clearFilters();
            const targets = resolveAddFanOut(scope);
            const codeText = suggestCreateCodeText({
                type: scope.type,
                targets,
            });
            const created = targets.map((target) => ({
                accountId: addAccountInScope({
                    entityId: target.entityId,
                    type: scope.type,
                    parentGroupId: target.parentGroupId,
                    position: target.position,
                    codeText,
                }),
                entityId: target.entityId,
                parentGroupId: target.parentGroupId,
                position: target.position,
            }));
            // Empty fan-out (empty workspace filter) mints nothing → no command.
            if (created.length > 0) {
                if (host === "workspace") {
                    onPendingAdd?.({
                        gestureId: created[0].accountId,
                        kind: "account",
                        accountType: scope.type,
                        targets: created.map((row) => ({
                            draftId: row.accountId,
                            entityId: row.entityId,
                            parentGroupId: row.parentGroupId,
                            position: row.position,
                        })),
                    });
                } else {
                    onOperation?.({
                        type: "addAccount",
                        created,
                        accountType: scope.type,
                        name: null,
                    });
                }
            }
            // Expand once, from the click: the host may be collapsed, and the
            // minted row is not in the tree yet to expand to. The focus hook
            // then scrolls to it and enters edit mode as it mounts.
            tableRef.current?.expandRowAndAncestors(scope.hostRowId);
            const focusId = created[0]?.accountId;
            if (focusId !== undefined) {
                requestFocus({ rowId: focusId, field: "name" });
            }
        },
        [
            addAccountInScope,
            clearFilters,
            host,
            onOperation,
            onPendingAdd,
            requestFocus,
            suggestCreateCodeText,
            tableRef,
        ],
    );

    const handleAddGroup = useCallback(
        (scope: NestedAddScope) => {
            // Per-row creation bypasses picker gating, so enforce the depth limit here.
            if (
                !canNestGroupUnderParent({
                    groups,
                    parentGroupId: scope.parentGroupId,
                })
            ) {
                errorToast(NESTED_GROUP_DEPTH_LIMIT_MESSAGE);
                return;
            }
            clearFilters();
            const created = resolveAddFanOut(scope).map((target) => ({
                groupId: addGroupInScope({
                    entityId: target.entityId,
                    type: scope.type,
                    parentGroupId: target.parentGroupId,
                    position: target.position,
                }),
                entityId: target.entityId,
                parentGroupId: target.parentGroupId,
                position: target.position,
            }));
            if (created.length > 0) {
                if (host === "workspace") {
                    onPendingAdd?.({
                        gestureId: created[0].groupId,
                        kind: "group",
                        accountType: scope.type,
                        targets: created.map((row) => ({
                            draftId: row.groupId,
                            entityId: row.entityId,
                            parentGroupId: row.parentGroupId,
                            position: row.position,
                        })),
                    });
                } else {
                    onOperation?.({
                        type: "addGroup",
                        created,
                        accountType: scope.type,
                        name: null,
                    });
                }
            }
            tableRef.current?.expandRowAndAncestors(scope.hostRowId);
            const focusId = created[0]?.groupId;
            if (focusId !== undefined) {
                requestFocus({ rowId: focusId, field: "name" });
            }
        },
        [
            addGroupInScope,
            clearFilters,
            errorToast,
            groups,
            host,
            onOperation,
            onPendingAdd,
            requestFocus,
            tableRef,
        ],
    );

    return { handleAddAccount, handleAddGroup };
};
